<template>
  <div class="ip-manage">
    <div class="chart-card">
      <div class="chart-header">
        <el-input v-model="keyword" placeholder="搜索IP地址" size="small" style="width:200px" clearable @clear="loadList" @keyup.enter.native="loadList" prefix-icon="el-icon-search" />
        <el-select v-model="blockStatus" placeholder="全部状态" size="small" style="width:120px;margin-left:10px" @change="loadList">
          <el-option label="全部" value="" />
          <el-option label="已禁用" value="blocked" />
          <el-option label="未禁用" value="unblocked" />
        </el-select>
        <el-select v-model="websiteId" placeholder="全部网站" clearable size="small" style="width:160px;margin-left:10px" @change="loadList">
          <el-option label="全部网站" :value="0" />
          <el-option v-for="w in websites" :key="w.id" :label="w.name" :value="w.id" />
        </el-select>
        <el-button type="primary" size="small" icon="el-icon-refresh" @click="loadList" style="margin-left:10px">刷新</el-button>
        <div class="header-right">
          <span class="stat-tag">共 {{ total }} 个IP</span>
          <span v-if="blockedCount > 0" class="stat-tag" style="color:#c76b6b;background:#fef2f2;border-color:#fde2e2">已禁用 {{ blockedCount }}</span>
          <el-pagination
            v-if="total > pageSize"
            small
            layout="prev, pager, next"
            :total="total"
            :page-size="pageSize"
            :current-page.sync="page"
            @current-change="loadList"
            style="margin-left:8px"
          />
        </div>
      </div>
      <div class="table-wrap">
        <el-table
          :data="list"
          v-loading="loading"
          stripe
          style="width:100%"
          size="small"
          @row-click="showDetail"
          highlight-current-row
          :header-cell-style="{background:'#f8fafc',color:'#64748b',fontWeight:600,fontSize:'12px'}"
          height="100%"
        >
          <el-table-column label="IP地址" width="170">
            <template slot-scope="{ row }">
              <span class="ip-link">{{ row.ip }}</span>
              <el-tag v-if="row.is_blocked" type="danger" size="mini" style="margin-left:6px">已禁用</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="ip_location" label="归属地" min-width="160" show-overflow-tooltip>
            <template slot-scope="{ row }">
              <i class="el-icon-location-outline" style="color:#94a3b8;margin-right:4px"></i>{{ row.ip_location || '未知' }}
            </template>
          </el-table-column>
          <el-table-column prop="visit_count" label="访问次数" width="100" align="right" sortable>
            <template slot-scope="{ row }"><span :class="{'num-hot': row.visit_count > 100}">{{ row.visit_count }}</span></template>
          </el-table-column>
          <el-table-column prop="unique_pages" label="页面数" width="80" align="right" sortable />
          <el-table-column prop="unique_visitors" label="访客数" width="80" align="right" sortable />
          <el-table-column prop="first_visit" label="首次访问" width="145" show-overflow-tooltip>
            <template slot-scope="{ row }"><span class="time-text">{{ formatTime(row.first_visit) }}</span></template>
          </el-table-column>
          <el-table-column prop="last_visit" label="最后访问" width="145" show-overflow-tooltip>
            <template slot-scope="{ row }"><span class="time-text">{{ formatTime(row.last_visit) }}</span></template>
          </el-table-column>
          <el-table-column v-if="isSuper" label="操作" width="120" align="center">
            <template slot-scope="{ row }">
              <el-button v-if="!row.is_blocked" type="danger" size="mini" icon="el-icon-lock" @click.stop="blockIp(row)">禁用</el-button>
              <el-button v-else type="success" size="mini" icon="el-icon-unlock" @click.stop="unblockIp(row)">解禁</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <el-drawer
      :title="`IP详情 - ${detailData.ip || ''}`"
      :visible.sync="detailVisible"
      direction="rtl"
      size="720px"
      append-to-body
      :wrapperClosable="true"
    >
      <div v-loading="detailLoading" v-if="detailData.ip" class="detail-body">
        <div class="detail-cards">
          <div class="dc-card dc-blue">
            <div class="dc-num">{{ detailData.summary.visit_count || 0 }}</div>
            <div class="dc-label">总访问</div>
          </div>
          <div class="dc-card dc-green">
            <div class="dc-num">{{ detailData.summary.unique_pages || 0 }}</div>
            <div class="dc-label">页面数</div>
          </div>
          <div class="dc-card dc-orange">
            <div class="dc-num">{{ detailData.summary.unique_visitors || 0 }}</div>
            <div class="dc-label">独立访客</div>
          </div>
          <div class="dc-card" :class="detailData.block_info && detailData.block_info.status ? 'dc-red' : 'dc-green'">
            <div class="dc-num" :style="{color: detailData.block_info && detailData.block_info.status ? '#c76b6b' : '#6b9e72'}">
              {{ detailData.block_info && detailData.block_info.status ? '禁用' : '正常' }}
            </div>
            <div class="dc-label">封禁状态</div>
          </div>
        </div>

        <div class="detail-section">
          <div class="section-title"><i class="el-icon-info"></i> 基本信息</div>
          <div class="info-grid">
            <div class="info-item"><span class="ikey">IP地址</span><span class="ival">{{ detailData.ip }}</span></div>
            <div class="info-item"><span class="ikey">归属地</span><span class="ival">{{ detailData.ip_location || '未知' }}</span></div>
            <div class="info-item"><span class="ikey">首次访问</span><span class="ival">{{ formatTime(detailData.summary.first_visit) }}</span></div>
            <div class="info-item"><span class="ikey">最后访问</span><span class="ival">{{ formatTime(detailData.summary.last_visit) }}</span></div>
            <div class="info-item"><span class="ikey">新访客数</span><span class="ival">{{ detailData.summary.new_visitors || 0 }}</span></div>
            <div class="info-item"><span class="ikey">关联网站</span><span class="ival">{{ detailData.summary.unique_websites || 0 }} 个</span></div>
          </div>
        </div>

        <div class="detail-section" v-if="detailData.block_info">
          <div class="section-title"><i class="el-icon-lock"></i> 封禁记录</div>
          <div class="info-grid">
            <div class="info-item">
              <span class="ikey">状态</span>
              <span class="ival"><el-tag :type="detailData.block_info.status ? 'danger' : 'success'" size="mini">{{ detailData.block_info.status ? '禁用中' : '已解禁' }}</el-tag></span>
            </div>
            <div class="info-item"><span class="ikey">原因</span><span class="ival">{{ detailData.block_info.reason || '无' }}</span></div>
            <div class="info-item"><span class="ikey">操作人</span><span class="ival">{{ detailData.block_info.blocked_by || '未知' }}</span></div>
            <div class="info-item"><span class="ikey">禁用时间</span><span class="ival">{{ formatTime(detailData.block_info.created_at) }}</span></div>
          </div>
        </div>

        <div class="detail-section">
          <div class="section-title"><i class="el-icon-monitor"></i> 环境分布</div>
          <div class="env-row">
            <div class="env-col" v-if="detailData.browser_stats && detailData.browser_stats.length">
              <div class="env-subtitle">浏览器</div>
              <div v-for="item in detailData.browser_stats" :key="item.browser" class="bar-item">
                <span class="bar-name">{{ item.browser || '未知' }}</span>
                <div class="bar-track"><div class="bar-fill" :style="{width: barWidth(item.cnt, detailData.browser_stats) + '%'}"></div></div>
                <span class="bar-cnt">{{ item.cnt }}</span>
              </div>
            </div>
            <div class="env-col" v-if="detailData.device_stats && detailData.device_stats.length">
              <div class="env-subtitle">设备类型</div>
              <div v-for="item in detailData.device_stats" :key="item.device" class="bar-item">
                <span class="bar-name">{{ item.device || '未知' }}</span>
                <div class="bar-track"><div class="bar-fill" :style="{width: barWidth(item.cnt, detailData.device_stats) + '%'}"></div></div>
                <span class="bar-cnt">{{ item.cnt }}</span>
              </div>
            </div>
            <div class="env-col" v-if="detailData.os_stats && detailData.os_stats.length">
              <div class="env-subtitle">操作系统</div>
              <div v-for="item in detailData.os_stats" :key="item.os" class="bar-item">
                <span class="bar-name">{{ item.os || '未知' }}</span>
                <div class="bar-track"><div class="bar-fill" :style="{width: barWidth(item.cnt, detailData.os_stats) + '%'}"></div></div>
                <span class="bar-cnt">{{ item.cnt }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="detail-section">
          <div class="section-title"><i class="el-icon-tickets"></i> 热门页面 Top 15</div>
          <el-table :data="detailData.top_pages" size="mini" style="width:100%" max-height="240" :header-cell-style="{background:'#f8fafc',fontSize:'12px'}">
            <el-table-column type="index" label="#" width="40" />
            <el-table-column label="页面" show-overflow-tooltip>
              <template slot-scope="{ row }"><span class="page-url">{{ row.page_url }}</span></template>
            </el-table-column>
            <el-table-column prop="cnt" label="次数" width="70" align="right" />
          </el-table>
        </div>

        <div class="detail-section">
          <div class="section-title"><i class="el-icon-link"></i> 来源域名 Top 10</div>
          <el-table :data="detailData.top_referers" size="mini" style="width:100%" max-height="200" :header-cell-style="{background:'#f8fafc',fontSize:'12px'}">
            <el-table-column type="index" label="#" width="40" />
            <el-table-column label="来源" show-overflow-tooltip>
              <template slot-scope="{ row }">{{ row.referer_domain || '直接访问' }}</template>
            </el-table-column>
            <el-table-column prop="cnt" label="次数" width="70" align="right" />
          </el-table>
        </div>

        <div class="detail-section">
          <div class="section-title"><i class="el-icon-time"></i> 访问路径（最近30次）</div>
          <div class="path-list">
            <div v-for="(item, idx) in detailData.access_path" :key="idx" class="path-item">
              <div class="path-time">{{ formatTime(item.created_at) }}</div>
              <div class="path-arrow"><i class="el-icon-right"></i></div>
              <div class="path-page">
                <span class="page-url">{{ item.page_url }}</span>
                <el-tag v-if="item.website_name" size="mini" style="margin-left:6px">{{ item.website_name }}</el-tag>
              </div>
            </div>
          </div>
        </div>

        <div class="detail-section">
          <div class="section-title"><i class="el-icon-document"></i> 最近访问记录（50条）</div>
          <el-table :data="detailData.recent_visits" size="mini" style="width:100%" max-height="360" :header-cell-style="{background:'#f8fafc',fontSize:'12px'}">
            <el-table-column label="时间" width="140">
              <template slot-scope="{ row }"><span class="time-text">{{ formatTime(row.created_at) }}</span></template>
            </el-table-column>
            <el-table-column label="页面" show-overflow-tooltip>
              <template slot-scope="{ row }"><span class="page-url">{{ row.page_url }}</span></template>
            </el-table-column>
            <el-table-column label="来源" width="110" show-overflow-tooltip>
              <template slot-scope="{ row }">{{ row.referer_domain || '-' }}</template>
            </el-table-column>
            <el-table-column label="浏览器" width="80" show-overflow-tooltip>
              <template slot-scope="{ row }">{{ row.browser || '-' }}</template>
            </el-table-column>
            <el-table-column label="设备" width="60">
              <template slot-scope="{ row }">{{ row.device || '-' }}</template>
            </el-table-column>
            <el-table-column label="新" width="40" align="center">
              <template slot-scope="{ row }">
                <el-tag v-if="row.is_new_visitor" type="success" size="mini">新</el-tag>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </el-drawer>

    <el-dialog :title="banDialog.title" :visible.sync="banDialog.visible" width="480px" append-to-body>
      <el-form label-width="80px" size="small">
        <el-form-item label="IP地址">
          <span style="font-weight:600;color:#1e293b">{{ banDialog.ip }}</span>
        </el-form-item>
        <el-form-item label="禁用原因">
          <el-input v-model="banDialog.reason" placeholder="例如：高频爬虫扫描（可选）" />
        </el-form-item>
        <el-form-item label="推送网站">
          <div class="push-sites">
            <el-checkbox :value="banDialog.allChecked" @change="banDialogAll">全选</el-checkbox>
            <el-checkbox-group v-model="banDialog.websiteIds" style="margin-top:6px">
              <el-checkbox v-for="w in banWebsites" :key="w.id" :label="w.id" style="margin-bottom:4px;display:block;margin-left:0">
                {{ w.name }}
                <span v-if="w.ban_api_url" style="color:#6b9e72;font-size:11px;margin-left:4px">已配置接口</span>
                <span v-else style="color:#94a3b8;font-size:11px;margin-left:4px">未配置接口</span>
              </el-checkbox>
            </el-checkbox-group>
            <div v-if="banWebsites.length === 0" style="color:#94a3b8;font-size:13px">暂无已配置封禁接口的网站</div>
          </div>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="banDialog.visible = false">取消</el-button>
        <el-button type="danger" @click="confirmBlock" :loading="banDialog.loading">{{ banDialog.action === 'block' ? '确认禁用' : '确认解禁' }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { statsApi, websiteApi } from '../api';
import dayjs from 'dayjs';

export default {
  name: 'IpManage',
  data() {
    return {
      list: [],
      total: 0,
      blockedCount: 0,
      page: 1,
      pageSize: 50,
      keyword: '',
      blockStatus: '',
      websiteId: 0,
      websites: [],
      loading: false,
      detailVisible: false,
      detailLoading: false,
      detailData: {},
      isSuper: false,
      banWebsites: [],
      banDialog: {
        visible: false,
        title: '',
        ip: '',
        reason: '',
        action: 'block',
        websiteIds: [],
        allChecked: false,
        loading: false,
      },
    };
  },
  async mounted() {
    try {
      const u = JSON.parse(localStorage.getItem('wt_user') || '{}');
      this.isSuper = u.is_super === 1;
    } catch (e) { this.isSuper = false; }
    await this.loadWebsites();
    this.loadList();
  },
  methods: {
    async loadWebsites() {
      try {
        const res = await websiteApi.list({ page: 1, page_size: 100 });
        if (res.code === 0) this.websites = res.data.list;
      } catch (e) { console.error(e); }
    },
    async loadList() {
      this.loading = true;
      try {
        const res = await statsApi.ipManageList({
          page: this.page,
          page_size: this.pageSize,
          keyword: this.keyword,
          block_status: this.blockStatus,
          website_id: this.websiteId,
        });
        if (res.code === 0) {
          this.list = res.data.list;
          this.total = res.data.total;
          this.blockedCount = res.data.blocked_count;
        }
      } catch (e) { console.error(e); }
      finally { this.loading = false; }
    },
    async showDetail(row) {
      this.detailVisible = true;
      this.detailLoading = true;
      this.detailData = {};
      try {
        const res = await statsApi.ipManageDetail({ ip: row.ip, website_id: this.websiteId });
        if (res.code === 0) {
          this.detailData = res.data;
        }
      } catch (e) { console.error(e); }
      finally { this.detailLoading = false; }
    },
    async loadBanWebsites() {
      try {
        const res = await websiteApi.list({ page: 1, page_size: 100 });
        if (res.code === 0) {
          this.banWebsites = res.data.list.filter(w => w.ban_api_url);
        }
      } catch (e) { console.error(e); }
    },
    async blockIp(row) {
      await this.loadBanWebsites();
      this.banDialog = {
        visible: true,
        title: '禁用IP - ' + row.ip,
        ip: row.ip,
        reason: '',
        action: 'block',
        websiteIds: this.banWebsites.map(w => w.id),
        allChecked: true,
        loading: false,
      };
    },
    async unblockIp(row) {
      await this.loadBanWebsites();
      this.banDialog = {
        visible: true,
        title: '解禁IP - ' + row.ip,
        ip: row.ip,
        reason: '',
        action: 'unblock',
        websiteIds: this.banWebsites.map(w => w.id),
        allChecked: true,
        loading: false,
      };
    },
    banDialogAll(val) {
      this.banDialog.websiteIds = val ? this.banWebsites.map(w => w.id) : [];
      this.banDialog.allChecked = val;
    },
    async confirmBlock() {
      this.banDialog.loading = true;
      try {
        const api = this.banDialog.action === 'block' ? statsApi.addIpBlocklist : statsApi.removeIpBlocklist;
        const res = await api({
          ip: this.banDialog.ip,
          reason: this.banDialog.reason || '',
          website_ids: this.banDialog.websiteIds,
        });
        if (res.code === 0) {
          this.$message.success(res.msg);
          this.banDialog.visible = false;
          this.loadList();
          this.loadBlockedIps();
        } else {
          this.$message.error(res.msg);
        }
      } catch (e) { this.$message.error('操作失败'); }
      finally { this.banDialog.loading = false; }
    },
    async loadBlockedIps() {
      try {
        const res = await statsApi.getBlockedIps();
        if (res.code === 0) {
          localStorage.setItem('wt_blocked_ips', JSON.stringify(res.data));
        }
      } catch (e) {}
    },
    barWidth(cnt, arr) {
      const max = Math.max(...arr.map(i => i.cnt), 1);
      return Math.round(cnt / max * 100);
    },
    formatTime(t) {
      if (!t) return '-';
      return dayjs(t).format('YYYY-MM-DD HH:mm:ss');
    },
  },
};
</script>

<style scoped>
.ip-manage {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.chart-card {
  background: #fff;
  border-radius: 8px;
  border: 1px solid #eef0f3;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.chart-header {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f1f3f5;
  flex-shrink: 0;
}
.table-wrap {
  flex: 1;
  overflow: hidden;
  min-height: 0;
}
.header-right {
  margin-left: auto;
  display: flex;
  gap: 8px;
  align-items: center;
}
.stat-tag {
  font-size: 12px;
  color: #64748b;
  background: #f8fafc;
  padding: 3px 10px;
  border-radius: 4px;
  border: 1px solid #eef0f3;
}
.ip-link {
  color: #5b8def;
  cursor: pointer;
  font-weight: 500;
}
.ip-link:hover {
  text-decoration: underline;
}
.num-hot {
  color: #c76b6b;
  font-weight: 600;
}
.time-text {
  color: #94a3b8;
  font-size: 12px;
}
.page-url {
  color: #475569;
  font-size: 12px;
  word-break: break-all;
}
.push-sites {
  background: #f8fafc;
  border-radius: 6px;
  padding: 10px 12px;
  border: 1px solid #eef0f3;
}

.detail-body {
  padding: 20px 24px;
}
.detail-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 24px;
}
.dc-card {
  background: #f8fafc;
  border-radius: 8px;
  padding: 16px 12px;
  text-align: center;
  border: 1px solid #eef0f3;
  border-left: 3px solid #5b8def;
}
.dc-blue { border-left-color: #5b8def; }
.dc-green { border-left-color: #6b9e72; }
.dc-orange { border-left-color: #d4905a; }
.dc-red { border-left-color: #c76b6b; }
.dc-num {
  font-size: 26px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
}
.dc-label {
  font-size: 11px;
  color: #64748b;
  margin-top: 4px;
}
.detail-section {
  margin-bottom: 20px;
}
.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f1f3f5;
}
.section-title i {
  color: #5b8def;
  margin-right: 6px;
}
.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px 20px;
}
.info-item {
  display: flex;
  align-items: center;
  font-size: 13px;
  line-height: 2;
}
.ikey {
  color: #94a3b8;
  width: 70px;
  flex-shrink: 0;
}
.ival {
  color: #475569;
  word-break: break-all;
}
.env-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}
.env-col {
  background: #f8fafc;
  border-radius: 8px;
  padding: 12px 14px;
  border: 1px solid #eef0f3;
}
.env-subtitle {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 8px;
}
.bar-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 3px 0;
}
.bar-name {
  font-size: 12px;
  color: #475569;
  width: 60px;
  flex-shrink: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.bar-track {
  flex: 1;
  height: 6px;
  background: #e2e8f0;
  border-radius: 3px;
  overflow: hidden;
}
.bar-fill {
  height: 100%;
  background: #5b8def;
  border-radius: 3px;
  transition: width 0.3s;
}
.bar-cnt {
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
  min-width: 24px;
  text-align: right;
}
.path-list {
  max-height: 300px;
  overflow-y: auto;
}
.path-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px dashed #f1f3f5;
}
.path-time {
  font-size: 12px;
  color: #94a3b8;
  white-space: nowrap;
  width: 135px;
  flex-shrink: 0;
}
.path-arrow {
  color: #cbd5e1;
  flex-shrink: 0;
}
.path-page {
  flex: 1;
  min-width: 0;
}
</style>
